import { useLayoutEffect } from "react";
import { localeDirection } from "@/lib/locale-direction";

/**
 * Applies the widget locale's text direction.
 *
 * - Shadow DOM embeds (web-component / React, `root` = the mount div): `dir="rtl"` for a right
 *   to left locale (ar, he); for any other locale the attribute is REMOVED so the widget keeps
 *   inheriting the host page's direction, as it always did (a `<html dir="rtl">` host with an
 *   English widget stays rtl). The host page itself is never touched.
 * - iframe (`root` undefined): the document element, always explicit, because the iframe has
 *   no host to inherit from.
 *
 * A layout effect so the first paint already has the right direction (no one-frame LTR flash
 * for Arabic). Driven by the resolved locale, so a runtime language switch flips it too.
 */
export function useLocaleDirection(locale: string, root: HTMLElement | undefined): void {
  useLayoutEffect(() => {
    const dir = localeDirection(locale);
    if (!root) {
      document.documentElement.dir = dir;
    } else if (dir === "rtl") {
      root.dir = "rtl";
    } else {
      root.removeAttribute("dir");
    }
  }, [locale, root]);
}
